<script setup>
import {
	Toggle,
	Tabs,
	Tooltip,
	Tab,
	Card,
	Label,
	Input,
	Spin,
	ProfilePicture,
	ColorPicker,
	Textarea,
	Select,
	Button,
	Icon,
	Modal,
	Checkbox,
	MediaUploaderButton,
} from "@components";
import { ref, reactive, computed, onMounted, watch, onUnmounted } from "vue";

// Stores.
import { useService } from "@store/service";
const $service = useService();

import { useServiceItem } from "@store/serviceItem";
const $serviceItem = useServiceItem();

import { useCategory } from "@store/category";
const $category = useCategory();

import { useStaff } from "@store/staff";
const $staff = useStaff();

import { useSettings } from "@store/settings";
const $settings = useSettings();

import { progress, getPrice, isInteger, validationCheck } from "@utils";

// Handle old and new service.
import { useRoute, useRouter } from "vue-router";

const route = useRoute();
const router = useRouter();

import EditGeneral from "./EditGeneral.vue";
import EditStaff from "./EditStaff.vue";
import EditDuration from "./EditDuration.vue";

// State.
const state = reactive({
	loading: true,
});

// Category.
const category = ref([]);

onMounted(async () => {
	await $serviceItem.mountServiceItem();
	$serviceItem.isServiceChanged = false
	router.replace({
		query: {
			...route.query,
			tab: "general",
		},
	});
});

// Actions.

const save = async (type) => {
	$serviceItem.submit(type);
	// if (route.query.dashboard) {
	//     router.push({ name: 'dashboard' })
	// }
};

const publish = async () => {
	// $serviceItem.service.status = 'publish'
	$serviceItem.state.saving = "publish";
	await save("publish");
};

const draft = async () => {
	// $serviceItem.service.status = 'draft'
	$serviceItem.state.saving = "draft";
	await save("draft");
};

const archive = async () => {
	let confirm = await Prompt.fire({
		title: `Archive ${String(
			$settings.labels.service?.singular
		).toLowerCase()}`,
		message: `Are you sure you want to archive this ${String(
			$settings.labels.service?.singular
		).toLowerCase()}?`,
		ok: "Yes, archive",
		cancel: "No, cancel",
	});
	if (!confirm.ok) return;
	// $serviceItem.service.status = 'archive'
	await save("archive");
};

const publishButton = computed(() => {
	if ($serviceItem.service.status !== "publish" || $serviceItem.isNew) {
		return $serviceItem.state.saving == "publish" ? "Publishing" : "Publish";
	}

	return $serviceItem.state.saving == "publish" ? "Updating" : "Update";
});

const draftButton = computed(() => {
	return $serviceItem.state.saving == "draft" ? "Saving" : "Save as draft";
});

// Progress.

const object_states = computed(() => {
	if (!$serviceItem.service.status) return [];
	let _states = [];

	if (
		$serviceItem.service.status &&
		$serviceItem.service.status !== "publish"
	) {
		_states.push(
			String($serviceItem.service.status).split("")[0].toUpperCase() +
				String($serviceItem.service.status).slice(1)
		);
	}

	if ($serviceItem.service.meta.is_private) {
		_states.push("Private");
	}

	return _states;
});

// Options.
// const $settings = prebook_admin.settings || {}

const deleteService = async () => {
	const response = await $service.deleteService(
		$serviceItem.service.service_id
	);

	if (response.success) {
		router.push({ name: "service.list" });
	}
};

const backButton = async () => {
	if ($serviceItem.isServiceChanged) {
		const choice = await Prompt.fire({
			title: 'Unsaved Changes Detected',
			message: `You have unsaved changes. Any unsaved changes will be lost. <br> Are you sure you want to continue?`,
			ok: 'Yes, proceed',
			cancel: 'No, cancel',
		});
		if (!choice.ok) return;
	}
	if (route.query.dashboard) {
		router.push({ name: "dashboard" });
	} else if (route.query.backTo) {
		router.push(route.query.backTo);
	} else {
		router.push({ name: "service.list" });
	}
};

const isDisabled = computed(() => {
	if ($serviceItem.state.loading || $serviceItem.state.saving)
		"pointer-events-none";
});

onUnmounted(() => {
	$serviceItem.resetService();
});
</script>

<template>
	<Modal :progress="progress('general, staff, price')" class="transition duration-100 bg-white">
		<!-- header  -->
		<template #header>
			<div class="px-8 py-6 border-b-[#EAECF0] border-b flex items-center justify-between shadow-[0px_3px_0px_0px_#eaecf0]">
				<div @click="backButton" class="cursor-pointer flex items-center gap-5 text-[#101828] text-lg font-semibold">
					<Icon name="arrowNarrowLeft" size="24px"></Icon>Back
				</div>
				<div v-if="!$serviceItem.state.loading" class="flex items-center h-10 gap-4">
					<Button size="lg" type="secondary_gray" radius="square" @click.prevent="draft">{{ draftButton }}</Button>
					<Button size="lg" type="primary" radius="square" @click.prevent="publish">{{ publishButton }}</Button>
					<div @click="backButton" class="cursor-pointer">
						<Icon name="xClose" size="20px"></Icon>
					</div>
				</div>
				<div v-else class="flex items-center h-10">
					<Spin size="25px" />
				</div>
			</div>
		</template>
		<!-- main  -->
		<div class="w-full max-w-[736px] m-auto" :class="{ 'cursor-wait': $serviceItem.state.loading || $serviceItem.state.saving }">
			<!-- page heading -->
			<div class="mt-[29px] mb-6 flex items-center justify-between gap-3 text-[#344054] text-2xl font-semibold leading-8">
				<div class="flex items-center gap-3">
					{{ $serviceItem.isNew ? 'Add' : 'Update' }} {{ String($settings.labels.service?.singular).toLowerCase() }}
					<div class="mt-2 text-xs" v-if="object_states.length">- {{ object_states.join(', ') }}</div>
				</div>
				<div class="flex items-center gap-3">
					<div v-if="$serviceItem.state.loading" class="animate-pulse bg-[#F2F4F7] rounded-full w-[115px] h-[24px]"></div>
					<Tooltip v-else :title="`Accessible to admins and ${String($settings.labels.staff?.plural).toLowerCase()}`" :subtitle="`When enabled, this ${String($settings.labels.service?.singular).toLowerCase()} won't be visible on the booking form for ${String($settings.labels.customer?.plural).toLowerCase()}`" positionX="left">
						<!-- {{ {
							type:typeof $serviceItem.service.meta.is_private,
							value: $serviceItem.service.meta.is_private,
							checked: $serviceItem.service.meta.is_private?'true':'false'
						} }} -->
						<Toggle v-model="$serviceItem.service.meta.is_private">Set Private</Toggle>
					</Tooltip>
				</div>
			</div>
			<!-- form tabs -->
			<Tabs default="general" :class="isDisabled">
				<Tab id="general">General</Tab>
				<Tab id="staff" v-if="$settings.addons.staffs">{{ $settings.labels.staff?.singular }}</Tab>
				<Tab id="price">Duration & Price</Tab>
				<!-- <Tab id="advance">Advance</Tab> -->

				<!-- General  -->
				<template #general>
					<form @submit.prevent="publish()">
						<Card cardClass="gap-5">
							<template #header>
								<div class="flex flex-col gap-1">
									<div class="text-[#101828] text-lg font-semibold capitalize">{{ $settings.labels.service?.singular }} Details</div>
									<div class="text-[#667085] text-sm font-normal">Enter {{ String($settings.labels.service?.singular).toLowerCase() }} name, description, category, and thumbnail</div>
								</div>
							</template>
							<template #default>
								<EditGeneral />
							</template>
						</Card>
						<!-- For Add Page -->
						<!-- <div v-if="$serviceItem.state.loading" class="flex items-center gap-10 mt-10">
                            <div v-for="i in 2" class="animate-pulse bg-[#F2F4F7] rounded-md h-[46px] w-[113px]"></div>
                            <div class="animate-pulse bg-[#F2F4F7] rounded-md h-[46px] w-[calc(100%-226px)]"></div>
						</div>-->
						<div class="flex items-center justify-between gap-5 mt-10">
							<div v-if="!$serviceItem.service" class="flex gap-2">
								<Button size="lg" type="tertiary_gray" radius="square" classButton="!w-[113px] justify-between text-[#667085]" @click="deleteService">
									<div class="w-[70%]">Delete</div>
									<template #before>
										<Icon name="trash01" size="20px"></Icon>
									</template>
								</Button>
								<Button size="lg" type="tertiary_gray" radius="square" classButton="!w-[113px] justify-between text-[#667085]" @click="archive">
									<div class="w-[70%]">Archive</div>
									<template #before>
										<Icon name="archive" size="20px"></Icon>
									</template>
								</Button>
							</div>
							<Button size="xl" type="primary" radius="square" classButton="w-full" @click="router.push({ query: { ...route.query, tab: $settings.addons.staffs ? 'staff' : 'price' } })">Continue</Button>
						</div>
					</form>
				</template>
				<!-- Staff  -->
				<template #staff>
					<div>
						<Card cardClass="gap-5">
							<template #header>
								<div class="flex flex-col gap-1">
									<div class="text-[#101828] text-lg font-semibold">{{ $settings.labels.staff?.singular }} Details</div>
									<div class="text-[#667085] text-sm font-normal">Assign {{ String($settings.labels.staff?.singular).toLowerCase() }} members for this {{ String($settings.labels.service?.singular).toLowerCase() }}</div>
								</div>
							</template>
							<template #default>
								<EditStaff />
							</template>
						</Card>

						<!-- <Card class="mt-5">
                            <div class="flex gap-[18px] items-center justify-between">
                                <div>
                                    <div class="text-[#101828] text-base font-medium mb-1">Can user pick random staff member?</div>
                                    <div class="text-[#667085] text-sm font-normal">Need Text</div>
                                </div>
                                <Toggle v-model="$serviceItem.service.meta.enable_random_staff"></Toggle>
                            </div>
						</Card>-->

						<!-- For add page -->
						<!-- <div v-if="$serviceItem.state.loading" class="flex items-center gap-10 mt-10">
                            <div class="animate-pulse bg-[#F2F4F7] rounded-md h-[46px] w-[180px]"></div>
                            <div class="animate-pulse bg-[#F2F4F7] rounded-md h-[46px] w-[calc(100%-180px)]"></div>
						</div>-->
						<div class="flex items-center gap-10 mt-10">
							<Button size="xl" type="secondary_gray" radius="square" classButton="!w-[180px] justify-between" @click="router.push({ query: { ...route.query, tab: 'general' } })">
								<div class="w-[70%]">Prev</div>
								<template #before>
									<Icon name="arrowNarrowLeft" size="20px"></Icon>
								</template>
							</Button>
							<Button size="xl" type="primary" radius="square" classButton="w-full justify-between" @click="router.push({ query: { ...route.query, tab: 'price' } })">
								<div class="w-[95%]">Next</div>
								<template #after>
									<Icon name="arrowNarrowRight" size="20px"></Icon>
								</template>
							</Button>
						</div>
					</div>
				</template>
				<!-- Price  -->
				<template #price>
					<form @submit.prevent="publish()">
						<!-- Duration and price -->
						<Card cardClass="gap-5 mb-5">
							<template #header>
								<div class="flex items-center justify-between">
									<div class="flex flex-col gap-1">
										<div class="text-[#101828] text-lg font-semibold">Duration & Price</div>
										<div class="text-[#667085] text-sm font-normal">Specify {{ String($settings.labels.service?.singular).toLowerCase() }} duration and price</div>
									</div>
								</div>
							</template>
							<template #default>
								<EditDuration />
							</template>
						</Card>
						<!-- Buffer Time -->
						<!-- <Card cardClass="gap-5 mb-5">
                            <template #header>
                                <div class="flex items-center justify-between">
                                    <div class="flex flex-col gap-1">
                                        <div class="text-[#101828] text-lg font-semibold">Buffer Time</div>
                                        <div class="text-[#667085] text-sm font-normal">Assign staffs to the service
                                            and manage commission</div>
                                    </div>
                                    <Toggle v-model="$serviceItem.service.meta.has_buffer_time"></Toggle>
                                </div>
                            </template>
                            <div class="grid items-center grid-cols-2 gap-5" v-if="$serviceItem.service.meta.has_buffer_time">
                                <div class="w-full">
                                    <Label>Before</Label>
                                    <Input min="0" v-model="$serviceItem.service.meta.pre_buffer" type="number" class="mt-1.5">
                                    <template #after>
                                        <Select placeholder="Select" :options="{ minutes: 'Min', hour: 'Hour' }" v-model="$serviceItem.service.meta.pre_buffer_unit" borderless class="w-20"></Select>
                                    </template>
                                    </Input>
                                </div>
                                <div class="w-full">
                                    <Label>After</Label>
                                    <Input min="0" v-model="$serviceItem.service.meta.post_buffer" type="number" class="mt-1.5">
                                    <template #after>
                                        <Select placeholder="Select" :options="{ minutes: 'Min', hour: 'Hour' }" v-model="$serviceItem.service.meta.post_buffer_unit" borderless class="w-20"></Select>
                                    </template>
                                    </Input>
                                </div>
                            </div>
						</Card>-->
						<!-- Deposit payment -->
						<!-- <Card cardClass="gap-5 mb-5">
                            <template #header>
                                <div class="flex items-center justify-between">
                                    <div class="flex flex-col gap-1">
                                        <div class="text-[#101828] text-lg font-semibold">Deposit Payment</div>
                                        <div class="text-[#667085] text-sm font-normal">Assign staffs to the service
                                            and manage commission</div>
                                    </div>
                                    <Toggle v-model="$serviceItem.service.meta.required_deposit"></Toggle>
                                </div>
                            </template>
                            <div class="grid items-center grid-cols-1 gap-5" v-if="$serviceItem.service.meta.required_deposit">
                                <div class="w-full">
                                    <Label>Deposit amount:</Label>
                                    <Input min="0"  :iconLeft="$serviceItem.service.meta.deposit_type !== 'percentage' ? $settings.payment.currency_symbol : '%'" v-model="$serviceItem.service.meta.deposit_amount" type="number" class="mt-1.5">
                                    <template #after>
                                        <Select placeholder="Select" :options="{ fixed: 'Fixed', percentage: 'Percentage' }" v-model="$serviceItem.service.meta.deposit_type" borderless class="w-28"></Select>
                                    </template>
                                    </Input>
                                </div>
                            </div>
						</Card>-->
						<!-- For add page -->
						<!-- <div v-if="$serviceItem.state.loading" class="flex items-center gap-10 mt-10">
                            <div class="animate-pulse bg-[#F2F4F7] rounded-md h-[46px] w-[180px]"></div>
                            <div class="animate-pulse bg-[#F2F4F7] rounded-md h-[46px] w-[calc(100%-180px)]"></div>
						</div>-->
						<div class="flex items-center gap-10 mt-10">
							<Button size="xl" type="secondary_gray" radius="square" classButton="!w-[180px] justify-between" @click="router.push({ query: { ...route.query, tab: $settings.addons.staffs ? 'staff' : 'general' } })">
								<div class="w-[70%]">Prev</div>
								<template #before>
									<Icon name="arrowNarrowLeft" size="20px"></Icon>
								</template>
							</Button>
							<!-- <Button size="xl" type="primary" radius="square" classButton="w-full justify-between" @click="router.push({query:{...route.query, tab: 'advance'}})">
                                <div class="w-[95%]">Next</div>
                                <template #after>
                                    <Icon name="arrowNarrowRight" size="20px"></Icon>
                                </template>
							</Button>-->
							<Button @click.prevent="publish" size="xl" submit type="primary" radius="square" classButton="w-full justify-between">
								<div class="w-[95%]">{{ publishButton }}</div>
								<!-- <template #after>
                                    <Icon name="arrowNarrowRight" size="20px"></Icon>
								</template>-->
							</Button>
						</div>
					</form>
				</template>
				<!-- Advance  -->
				<!-- <template #advance>
                    <form @submit.prevent="publish()">
                        <Card cardClass="gap-5 mb-5">
                            <template #header>
                                <div class="flex items-center justify-between">
                                    <div class="flex flex-col gap-1">
                                        <div class="text-[#101828] text-lg font-semibold">Booking Type</div>
                                        <div class="text-[#667085] text-sm font-normal">Assign staffs to the service and manage commission</div>
                                    </div>
                                    <Toggle v-model="$serviceItem.service.meta.has_booking_type"></Toggle>
                                </div>
                            </template>
                            <div class="flex w-full gap-5" v-if="$serviceItem.service.meta.has_booking_type">
                                <Button v-for="booking_type in ['Onsite', 'Virtual', 'Home Service']" type="secondary_gray" size="md" radius="rounded" class="w-full" @click="toggleBookingType(booking_type);" :class="{ 'border border-[#D0D5DD] text-[#7F56D9] shadow-[0px_0px_0px_4px_#F2F4F7]': $serviceItem.service.meta.booking_type.includes(booking_type) }">{{ booking_type }}</Button>
                            </div>
                        </Card>
                        <div class="flex items-center gap-10">
                            <Button size="xl" type="secondary_gray" radius="square" classButton="!w-[180px] mt-10 justify-between" @click.prevent="router.push({query:{...route.query, tab: 'advance'}})">
                                <div class="w-[70%]">Back</div>
                                <template #before>
                                    <Icon name="arrowNarrowLeft" size="20px"></Icon>
                                </template>
                            </Button>
                            <Button @click.prevent="publish" size="xl" submit type="primary" radius="square" classButton="w-full mt-10 justify-between">
                                <div class="w-[95%]">{{ publishButton }}</div>
                                <template #after>
                                    <Icon name="arrowNarrowRight" size="20px"></Icon>
                                </template>
                            </Button>
                        </div>
                    </form>
				</template>-->
			</Tabs>
		</div>
	</Modal>
</template>